import React from 'react';
import { Disclosure, Transition } from '@headlessui/react'
import { ChevronUpIcon } from '@heroicons/react/20/solid'

const FLAVOR_ATTR_ORDER = ['flavor', 'flavor_profile', 'ice_level', 'sweet_level'];
const VARIANT_ATTR_ORDER = ['count', 'volume', 'nicotine_concentration_per_ml', 'nicotine_concentration_percentage'];

const EnrichedProductDetail = ({ 
  title, 
  uniqueProductAttributes, 
  uniqueVariantAttributes, 
  isEditing, 
  Field, 
  BooleanField, 
  onChange, 
  onCheckboxChange,
  onVariantChange,
  onVariantCheckboxChange,
  productIndex,
  approveProductAttributes,
  approveVariantAttributes
 }) => {

  const flatVariantAttrs = Object.values(uniqueVariantAttributes).flat();

  const allAttributes = [
    ...uniqueProductAttributes,
    ...flatVariantAttrs
  ];
  
  const approvedCount = allAttributes.filter(attr => attr.approved && attr.value?.toString().trim() !== '').length;
  const totalCount = allAttributes.length;

  return (
    <Disclosure as="div" className="border rounded-md bg-white shadow-sm mt-2">
      {({ open }) => (
        <>
          <Disclosure.Button className="flex justify-between w-full px-4 py-3 text-sm font-medium text-left text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus-visible:ring focus-visible:ring-indigo-500 focus-visible:ring-opacity-75">
            <span>{title}</span>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-600">
                {approvedCount} / {totalCount} Approved
              </span>
              <ChevronUpIcon
                className={`${
                  open ? 'rotate-180 transform' : ''
                } h-5 w-5 text-indigo-500`}
              />
            </div>
          </Disclosure.Button>
          <Transition
            show={open}
            enter="transition-all duration-300 ease-out"
            enterFrom="max-h-0 opacity-0"
            enterTo="max-h-[1000px] opacity-100"
            leave="transition-all duration-200 ease-in"
            leaveFrom="max-h-[1000px] opacity-100"
            leaveTo="max-h-0 opacity-0"
          >
            <Disclosure.Panel className="overflow-hidden px-4 pt-4 pb-6 text-sm text-gray-700">
            <div className="space-y-4">
              <div>
                <h5 className="text-gray-500 text-sm uppercase mb-1">Flavor Details</h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  {FLAVOR_ATTR_ORDER.map(attrName => {
                    const attr = uniqueProductAttributes.find(a => a.attribute_name === attrName);
                    return attr ? (
                      <div key={attr.id}>
                        {attr.value === 'true' || attr.value === 'false' ? (
                            <BooleanField
                              attr={attr}
                              isEditing={isEditing}
                              onChange={(id, key, value) => onChange(productIndex, id, value)}
                              onCheckboxChange={(id, key, value) => onCheckboxChange(productIndex, id, key, value)}
                            />
                        ) : (
                            <Field 
                              attr={attr} 
                              isEditing={isEditing}
                              onChange={(id, key, value) => onChange(productIndex, id, value)}
                              onCheckboxChange={(id, key, value) => onCheckboxChange(productIndex, id, key, value)}
                            />
                        )}
                      </div>
                    ) : null;
                  })}
                </div>
                <div className="mt-4">
                  {uniqueProductAttributes
                    .filter(attr => attr.attribute_name === 'flavor_description')
                    .map(attr => (
                      <div key={attr.id} className="col-span-full">
                        <Field 
                          attr={attr} 
                          isEditing={isEditing}
                          onChange={(id, key, value) => onChange(productIndex, id, value)}
                          onCheckboxChange={(id, key, value) => onCheckboxChange(productIndex, id, key, value)}
                        />
                      </div>
                    ))}
                    <button
                      onClick={() => approveProductAttributes(productIndex)}
                      className="mt-2 text-sm text-indigo-600 hover:underline"
                      hidden={!isEditing}
                    >
                      Approve Flavor Details
                    </button>
                </div>
              </div>
              {/* Variant Details */}
              <div>
                <h5 className="text-gray-500 text-sm uppercase mb-1">Variant Details</h5>
                {Object.entries(uniqueVariantAttributes).map(([variantId, attrs], index) => (
                  <div key={variantId} className="border rounded-md p-4 mt-4 bg-gray-50">
                    <h6 className="text-sm font-medium text-gray-600 mb-3">
                      Variant {index + 1} <span className="text-xs text-gray-400">(ID: {variantId})</span>
                    </h6>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                      {VARIANT_ATTR_ORDER.map(attrName => {
                        const attr = attrs.find(a => a.attribute_name === attrName);
                        return attr ? (
                          <div key={attr.id}>
                            {attr.value === 'true' || attr.value === 'false'? (
                                <BooleanField
                                  attr={attr}
                                  isEditing={isEditing}
                                  onChange={(id, key, value) => onVariantChange(productIndex, variantId, id, value)}
                                  onCheckboxChange={(id, key, value) => onVariantCheckboxChange(productIndex, variantId, id, key, value)}
                                />
                            ) : (
                                <Field 
                                  attr={attr} 
                                  isEditing={isEditing}
                                  onChange={(id, key, value) => onVariantChange(productIndex, variantId, id, value)}
                                  onCheckboxChange={(id, key, value) => onVariantCheckboxChange(productIndex, variantId, id, key, value)}
                                />
                            )}
                          </div>
                        ) : null;
                      })}
                    </div>
                    <button
                      onClick={() => approveVariantAttributes(productIndex, variantId)}
                      className="mt-2 text-sm text-indigo-600 hover:underline"
                      hidden={!isEditing}
                    >
                      Approve Variant {index + 1}
                    </button>
                  </div>
                ))}
              </div>
            </div>
            </Disclosure.Panel>
          </Transition>
        </>
      )}
    </Disclosure>
  );
};

export default EnrichedProductDetail;